import h from 'virtual-dom/h';
import objectPath from 'object-path';
import {DefaultSettings, settings} from '../../../settings/settings-util';
import { allowNav, shouldNav } from '../../../util/dnn';
import { sendRequest } from '../../lib-alme-request/src/lib-alme-request';
import {Reducer, Store} from "redux";
import {AlmeState, defaultState} from "../../../wireup/AlmeState";
import {VChild, VNode} from "virtual-dom";
import {HistoryMessage, HistoryMessageAlme} from "../../../types/history";
import {NavUrl} from "@alme/types/response/NavUrl";
import {Dictionary, Mutator} from "../../../types/rendering";
import {Message} from "../../../types/response";
import {setMinimize} from "../../window-minimize/src/actions/actions";
import {Input} from "../../../types/input";

type StepOutIcon = { renderIcon: (url: string) => VNode | undefined };
type MutatorModule = { getMutator: () => Mutator };

export default class NavUrlRenderer
{

    _settings: DefaultSettings;
    _store: Store<AlmeState> | null = null;
    _stepOutIcon: StepOutIcon | null;
    _navPromise: Promise<Message> | null = null;
    _mutatorModules: MutatorModule[];
    _mutators: Mutator[] = [];


    //#region Public Interface

    constructor(passedSettings?: DefaultSettings,
                stepOutIcon: StepOutIcon | null = null,
                mutatorModules: MutatorModule[] = [])
    {
        this._settings = passedSettings ?? settings;
        this._stepOutIcon = stepOutIcon;
        this._mutatorModules = mutatorModules;
    }

    static getDefaultSettings(): DefaultSettings
    {
        return settings;
    }

    initialize(store: Store<AlmeState>): void
    {
        this._store = store;
        this._mutators = this._mutatorModules.reduce((mutators, module) => [...mutators, module.getMutator()], [] as Mutator[]);
    }

    getReducer(): Reducer<AlmeState>
    {
        return (state = defaultState): AlmeState => state;
    }

    doesRender(message: HistoryMessage): boolean
    {
        const navUrl = (message as HistoryMessageAlme).navUrl;

        return navUrl?.LinkNavigationType === "MainPage" || navUrl?.LinkNavigationType === "Popup";
    }

    getRenderer()
    {
        return (message: HistoryMessage): VChild =>
        {
            if (!this.doesRender(message)) { return []; }

            const navUrl = (message as HistoryMessageAlme).navUrl as NavUrl;
            const isPopupLink = navUrl.LinkNavigationType === "Popup";
            const shouldNavigate: boolean = shouldNav(this.getWindowLocation(), this._settings);
            const enableCurrentPageMatching = this._settings.navConstants.enableCurrentPageMatching;

            let properties: Dictionary =
            {
                className: this._settings.classNames.navLinkMainPage,
                href: shouldNavigate ? navUrl.UrlAddress : '',
                target: isPopupLink ?  "_blank" : "_self",
                title: objectPath.get(navUrl, this._settings.navConstants.hoverTextPath) ?? "",
                onclick: (e: Event): boolean =>
                {
                    if (enableCurrentPageMatching && this.linkUrlMatchesCurrentPage(navUrl.UrlAddress))
                    {
                        const popper = (e.target as HTMLElement)?.previousSibling as HTMLElement;
                        const responseText = popper?.previousSibling as HTMLElement;
                        if (popper && responseText)
                        {
                            const spanHeight = responseText.clientHeight ?? popper.parentElement?.clientHeight;
                            popper.style.display = (popper.style.display === "none") ? "block" : "none";
                            popper.style.top = `${spanHeight - this._settings.Popper.responseOffset}px`;
                        }
                    }
                    else
                    {
                        if(this.isDownloadlink(navUrl)) {
                            this.urlOnClickReporting(navUrl);
                            return true;
                        }
                        else
                        {
                        this.urlOnClick(navUrl, isPopupLink);
                        }
                    }

                    e.stopPropagation();

                    return false;
                },
                onblur: (e: Event): void =>
                {
                    const popper = (e.target as HTMLElement)?.previousSibling as HTMLElement;
                    if (popper?.style)
                    {
                        popper.style.display = "none";
                    }
                },
            };

            if(navUrl.DisplayText === ""){
                properties["className"] = this._settings.classNames.navLinkMainPageHidden;
            }

            if (this.shouldDisable())
            {
                properties.href = '/';
                properties.onclick = (): boolean => false;
                properties.tabIndex = -1;
            }
            if(this.isDownloadlink(navUrl))
            {
                properties.download= navUrl.Metadata?.Download
            }
            // run mutators to update properties
            properties = this._mutators.reduce((props, mutate) => mutate(props), properties);

            const friendlyLinkName = (navUrl.Metadata?.UrlName as string) ?? navUrl.DisplayText;
            const linkText = shouldNavigate ? navUrl.DisplayText : friendlyLinkName;
            const stepOutIconNode = this._stepOutIcon?.renderIcon(navUrl.UrlAddress);

            return [this.createPopper(), h('a', properties, stepOutIconNode ? [linkText, stepOutIconNode] : linkText)];
        };
    }

    //#endregion

    isDownloadlink(link: NavUrl)
    {
        const isDownloadLink = link.Metadata?.Download ? true : false;
        return isDownloadLink;
    }
    urlOnClickReporting(link: NavUrl): Promise<Message>
    {
        const input:Input =
        {
            eventName: 'UrlClicked',
            eventValue: link.UrlAddress as string,
            messageType: this._settings.messageConstants.reportingEventRequest,
        };

        return sendRequest(input);
    }

    //#region Helper Methods

    urlOnClick(navUrl: NavUrl, isPopup: boolean): void
    {
        this.minimizeAgentInMobileView();
        const input =
        {
            eventName: 'UrlClicked',
            eventValue: navUrl.UrlAddress,
            messageType: this._settings.messageConstants.reportingEventRequest,
        };

        this._navPromise = sendRequest(input);
        this._navPromise.then(() =>
        {
            if (!isPopup && allowNav(this.getWindowLocation(), this._settings))
            {
                this.setWindowLocationHref(navUrl.UrlAddress);
            }
        });

        if (isPopup)
        {
            const newWin = window.open(navUrl.UrlAddress, '_blank');
            newWin?.focus();
        }

    }

    createPopper(): VNode
    {
        const { navLinkPopoverArrow, navLinkPopoverHeader, navLinkPopoverBody } = this._settings.classNames;
        const { popperHeader, popperBody } = this._settings.textConstants;

        return h('div', {className: this._settings.classNames.navLinkPopover, style: { display: 'none'}}, 
        [
            h('div', {className: navLinkPopoverArrow}, []),
            h('h3', {className: navLinkPopoverHeader}, [popperHeader]),
            h('div', {className: navLinkPopoverBody}, [popperBody]),
        ]);
    }
    
    linkUrlMatchesCurrentPage(url: string): boolean
    {
        if (url.startsWith("#")) { return false; }

        const windowLocation = this.getWindowLocation();
        const link = document.createElement("a");
        link.href = url;

        return link.hostname === windowLocation.hostname && link.pathname === windowLocation.pathname;
    }

    minimizeAgentInMobileView(): void
    {
        const windowWidth = window.innerWidth ?? document.documentElement.clientWidth ?? document.body.clientWidth;

        if (windowWidth <= this._settings?.Mobile?.breakpoint ?? -1)
        {
            this._store?.dispatch(setMinimize(true, this._settings.stateLocations.Window.IsMinimized));
        }
    }

    getWindowLocation(): Location
    {
        return window.location;
    }

    setWindowLocationHref(url: string): void
    {
        window.location.href = url;
    }

    shouldDisable(): boolean
    {
        const { Base, NavUrl } = this._settings.stateLocations.Window;

        return this._store?.getState()[Base][NavUrl.Base][NavUrl.shouldDisable];
    }

    //#endregion
}



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/message-renderer-nav-url/src/message-renderer-nav-url.ts